import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CopyPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BAR = { over: "bg-red-600", warn: "bg-amber-500", ok: "bg-emerald-500", none: "bg-muted" };
const LABEL = { over: "Over budget", warn: "80%+ used", ok: "On track", none: "No budget" };
const TXT = { over: "text-red-400", warn: "text-amber-400", ok: "text-emerald-400", none: "text-muted-foreground" };

const NoteBox = ({ r, year, canNote, onSaved }) => {
  if (!canNote && !r.note) return null;
  if (!canNote) return <p data-testid={`budget-note-${r.bike_id}`} className="col-span-2 rounded-lg bg-secondary px-3 py-2 text-xs sm:col-span-4"><b>Reason:</b> {r.note.text} <span className="text-muted-foreground">- {r.note.by}</span></p>;
  if (r.state !== "warn" && r.state !== "over" && !r.note) return null;
  const save = async (text) => {
    if (text.trim() === (r.note?.text || "")) return;
    try {
      await api.put(`/trustee/budgets/${year}/${r.bike_id}/note`, { text });
      toast.success(text.trim() ? "Reason saved" : "Reason removed");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <input key={`${r.bike_id}-${r.note?.text || ""}`} defaultValue={r.note?.text || ""} maxLength={1000} onBlur={(e) => save(e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      placeholder={r.state === "over" ? "Why is this bike over budget? e.g. accident damage, new tyres" : "Optional: reason for high spend"}
      data-testid={`budget-note-input-${r.bike_id}`} className={`col-span-2 h-9 rounded-md border bg-background px-3 text-xs sm:col-span-4 ${r.state === "over" && !r.note ? "border-red-500/60" : ""}`} />
  );
};

const BudgetRow = ({ r, year, canEdit, canNote, onSaved }) => {
  const save = async (val) => {
    const amount = val === "" ? null : Number(val);
    if (amount === r.budget || (amount !== null && (Number.isNaN(amount) || amount < 0))) return;
    try {
      await api.put(`/trustee/budgets/${year}/${r.bike_id}`, { amount });
      toast.success(`${r.bike} budget ${amount === null ? "cleared" : "saved"}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`budget-row-${r.bike_id}`} className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5 px-5 py-3 sm:grid-cols-[180px_130px_1fr_110px]">
      <div className="font-semibold">{r.bike}</div>
      {canEdit ? (
        <input key={`${r.bike_id}-${r.budget}`} type="number" min="0" step="50" inputMode="decimal" defaultValue={r.budget ?? ""} placeholder="Set £"
          onBlur={(e) => save(e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          data-testid={`budget-input-${r.bike_id}`} className="h-9 w-[120px] rounded-md border bg-background px-2 text-right font-mono text-sm" />
      ) : <div className="text-right font-mono text-sm">{r.budget !== null ? gbp(r.budget) : "-"}</div>}
      <div className="col-span-2 sm:col-span-1">
        <div className="h-2.5 overflow-hidden rounded-full bg-secondary">
          <div className={`h-full ${BAR[r.state]}`} style={{ width: `${Math.min(r.pct ?? 0, 100)}%` }} />
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">{gbp(r.spent)} spent{r.budget !== null ? ` · ${r.remaining >= 0 ? `${gbp(r.remaining)} left` : `${gbp(-r.remaining)} over`}` : ""}</div>
      </div>
      <div data-testid={`budget-state-${r.bike_id}`} className={`col-span-2 text-xs font-bold uppercase sm:col-span-1 sm:text-right ${TXT[r.state]}`}>{LABEL[r.state]}{r.pct !== null ? ` · ${r.pct}%` : ""}</div>
      <NoteBox r={r} year={year} canNote={canNote} onSaved={onSaved} />
    </div>
  );
};

export const BudgetPanel = ({ canEdit, canNote }) => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [uplift, setUplift] = useState("0");
  const load = () => api.get("/trustee/budgets", { params: { year } }).then((r) => { if (Number(r.config.params.year) === year) setRows(r.data); });
  useEffect(() => { setRows(null); load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = rows?.filter((r) => r.budget !== null) || [];
  const tb = set.reduce((a, r) => a + r.budget, 0);
  const ts = set.reduce((a, r) => a + r.spent, 0);
  const copy = async () => {
    const pct = Number(uplift || 0);
    if (Number.isNaN(pct) || pct < -50 || pct > 100) return toast.error("Increase must be between -50% and 100%");
    try {
      const { data: next } = await api.get("/trustee/budgets", { params: { year: year + 1 } });
      const clash = next.filter((r) => r.budget !== null).length;
      const overwrite = clash > 0 && window.confirm(`${year + 1} already has ${clash} budget${clash > 1 ? "s" : ""}. Replace them with the ${year} amounts${pct ? ` ${pct > 0 ? "+" : ""}${pct}%` : ""}? Press Cancel to keep them and only fill the gaps.`);
      const { data } = await api.post(`/trustee/budgets/${year}/copy-forward`, null, { params: { overwrite, uplift: pct } });
      toast.success(`Copied ${data.copied} budget${data.copied === 1 ? "" : "s"} to ${data.to_year}${pct ? ` with ${pct > 0 ? "+" : ""}${pct}%` : ""}`, { description: data.skipped ? `${data.skipped} kept as already set` : undefined });
      setCopyOpen(false);
      setYear(data.to_year);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section id="budgets" data-testid="budget-panel" className="scroll-mt-20 overflow-hidden rounded-2xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <h3 className="font-bold">Yearly repair budget</h3>
          <p className="text-xs text-muted-foreground">Repairs, brakes and tyres spend, Jan to Dec. {canEdit ? "Type a budget and press Enter. Clear the box to remove it." : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          {set.length > 0 && <span data-testid="budget-total" className={`text-sm font-bold ${ts > tb ? "text-red-400" : "text-emerald-400"}`}>{gbp(ts)} of {gbp(tb)}</span>}
          {canEdit && set.length > 0 && <button onClick={() => setCopyOpen((o) => !o)} data-testid="budget-copy-btn" className="inline-flex items-center gap-1.5 rounded-full border-2 border-sky-500 px-3 py-1.5 text-xs font-bold text-sky-300 hover:bg-sky-500/10"><CopyPlus className="h-3.5 w-3.5" /> Copy to {year + 1}</button>}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="budget-year-select" className="h-9 rounded-full border bg-background px-3 text-sm">
            {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {copyOpen && canEdit && (
        <div data-testid="budget-copy-panel" className="flex flex-wrap items-center gap-2 border-b bg-sky-500/5 px-5 py-3 text-sm">
          <span className="font-semibold">Copy {year} budgets to {year + 1} with an increase of</span>
          {["0", "3", "5", "10"].map((p) => <button key={p} onClick={() => setUplift(p)} data-testid={`uplift-${p}`} className={`rounded-full border-2 px-3 py-1 text-xs font-bold ${uplift === p ? "border-sky-500 bg-sky-500 text-white" : "text-muted-foreground"}`}>{p}%</button>)}
          <input type="number" min="-50" max="100" step="0.5" value={uplift} onChange={(e) => setUplift(e.target.value)} data-testid="uplift-input" className="h-8 w-20 rounded-md border bg-background px-2 text-right font-mono text-sm" />
          <span className="text-xs text-muted-foreground">e.g. {gbp(1000)} becomes {gbp(1000 * (1 + Number(uplift || 0) / 100))}</span>
          <button onClick={copy} data-testid="budget-copy-confirm" className="ml-auto rounded-full bg-sky-500 px-4 py-1.5 text-xs font-bold text-white">Copy budgets</button>
        </div>
      )}
      <div className="divide-y">
        {rows === null ? <div className="h-24 animate-pulse" /> : rows.map((r) => <BudgetRow key={r.bike_id} r={r} year={year} canEdit={canEdit} canNote={canNote} onSaved={load} />)}
      </div>
    </section>
  );
};
